import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

/* ─── Fórmulas ─────────────────────────────────────────────────────────────── */
// remark-math solo trata `$$` como bloque si va en líneas propias; el LLM suele
// escribir `$$T(n) = …$$` en una sola línea, que llega como math inline. Todo lo
// escrito con `$$` se renderiza en modo display (centrado, tamaño completo).
function remarkDisplayMath() {
  return (tree: any, file: any) => {
    const source = String(file);
    const walk = (node: any) => {
      if (node.type === 'inlineMath' && node.position && source.startsWith('$$', node.position.start.offset)) {
        node.data.hProperties.className = ['language-math', 'math-display'];
      }
      node.children?.forEach(walk);
    };
    walk(tree);
  };
}

/* ─── Vallas de código pegadas a texto ─────────────────────────────────────── */
// Valla en su propia línea: sangría, valla y lo que la siga.
const FENCE_LINE = /^([ \t]*)(`{3,}|~{3,})(.*)$/;
// Valla pegada a texto previo: "Fíjate:```python", "v[j + 1]:= valor```".
const GLUED_FENCE = /^([ \t]*)([^`]*?\S)[ \t]*(`{3,})(.*)$/;
// Info string con pinta de lenguaje ("python", "c++", "").
const LANG_TAG = /^[\w+#.-]*$/;

// El LLM a veces pega texto a las vallas, p. ej. la cita tras el cierre:
// "``` [Tema 2.pdf, pág 33]". En CommonMark esa línea no cierra el bloque y el
// resto de la respuesta acaba dentro del código, así que se separan valla y
// texto en líneas distintas.
function separateFences(md: string): string {
  const out: string[] = [];
  let open = ''; // valla que abrió el bloque en curso ('' = fuera de bloque)

  for (const line of md.split(/\r?\n/)) {
    const fence = line.match(FENCE_LINE);
    const glued = fence ? null : line.match(GLUED_FENCE);

    if (!open) {
      if (fence) {
        // Con backticks en la info string no es una valla, sino código inline.
        if (!(fence[2][0] === '`' && fence[3].includes('`'))) open = fence[2];
        out.push(line);
      } else if (glued && LANG_TAG.test(glued[4].trim())) {
        const [, indent, text, ticks, info] = glued;
        out.push(indent + text, indent + ticks + info.trim());
        open = ticks;
      } else {
        out.push(line);
      }
      continue;
    }

    const rest = (fence ? fence[3] : glued?.[4] ?? '').trim();
    const closes = fence
      ? fence[2][0] === open[0] && fence[2].length >= open.length && !(rest && LANG_TAG.test(rest))
      : glued && open[0] === '`' && glued[3].length >= open.length;

    if (!closes) {
      out.push(line);
      continue;
    }
    const indent = (fence ?? glued)[1];
    if (glued) out.push(indent + glued[2]);
    out.push(indent + open);
    if (rest) out.push(indent + rest);
    open = '';
  }
  return out.join('\n');
}

/**
 * Render compartido de markdown con la misma configuración de componentes que
 * usa el chat. Aplica la clase `.msg-assistant-body` (definida en ChatWindow /
 * vistas que lo monten) para heredar el estilado tipográfico.
 */
export default function MarkdownView({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath, remarkDisplayMath]}
      rehypePlugins={[rehypeKatex]}
      components={{
        p: ({ children }) => <p>{children}</p>,
        h1: ({ children }) => <h1>{children}</h1>,
        h2: ({ children }) => <h2>{children}</h2>,
        h3: ({ children }) => <h3>{children}</h3>,
        ul: ({ children }) => <ul>{children}</ul>,
        ol: ({ children }) => <ol>{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        strong: ({ children }) => <strong>{children}</strong>,
        em: ({ children }) => <em>{children}</em>,
        blockquote: ({ children }) => <blockquote>{children}</blockquote>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ),
        code: ({ children, className }) =>
          className ? <code className={className}>{children}</code> : <code>{children}</code>,
        pre: ({ children }) => <pre>{children}</pre>,
      }}
    >
      {separateFences(content)}
    </ReactMarkdown>
  );
}
